import { Price } from "@/types/price";
import {
    endOfDay,
    endOfHour,
    endOfMinute,
    endOfSecond,
    formatISO,
} from "date-fns";
import { useEffect, useState } from "react";
import pluralize from "./plural";

export function prettyTimeSince(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();

    const diff = now.getTime() - date.getTime();

    if (diff < 60000) {
        return "Just now";
    }

    if (diff < 3600000) {
        return `${Math.floor(diff / 60000)}m ago`;
    }

    if (diff < 86400000) {
        return `${Math.floor(diff / 3600000)}h ago`;
    }

    return formatISO(date, { representation: "date" });
}

export function longPrettyTimeSince(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();

    const diff = now.getTime() - date.getTime();

    if (diff < 60000) {
        return "Just now";
    }

    if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${pluralize(minutes, "minute", "minutes")} ago`;
    }

    if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${pluralize(hours, "hour", "hours")} ago`;
    }

    if (diff < 1000 * 60 * 60 * 24 * 365) {
        const days = Math.floor(diff / 86400000);
        return `${pluralize(days, "day", "days")} ago`;
    }

    return formatISO(date, { representation: "date" });
}

/**
 * Returns a pretty timezone abbreviation. For example, EST, EDT, PDT, PST.
 * If no date is given it will use the current date.
 * Examples:
 *   For timezone "America/New_York" and date "2024-11-30T10:00:00Z" it will return "EST".
 *   For timezone "America/New_York" and date "2024-06-30T10:00:00Z" it will return "EDT".
 * @param timezone
 * @param date
 */
export function prettyTimezone(
    timezone: string,
    date: Date = new Date(),
): string {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZoneName: "short",
        timeZone: timezone,
    });
    const formattedDate = formatter.formatToParts(date);
    const prettyTimeZone = formattedDate.find(
        (part) => part.type === "timeZoneName",
    );
    return prettyTimeZone?.value ?? "";
}

export function prettyTimezoneLong(
    timezone: string,
    date: Date = new Date(),
): string {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZoneName: "long",
        timeZone: timezone,
    });
    const formattedDate = formatter.formatToParts(date);
    const prettyTimeZone = formattedDate.find(
        (part) => part.type === "timeZoneName",
    );
    return prettyTimeZone?.value ?? "";
}

export function useTimestamp(
    refreshEvery: "second" | "minute" | "hour" | "day",
): number {
    const [now, setTimestamp] = useState(Date.now());

    useEffect(() => {
        const endOfUnit: Record<typeof refreshEvery, typeof endOfSecond> = {
            second: endOfSecond,
            minute: endOfMinute,
            hour: endOfHour,
            day: endOfDay,
        };
        let timeUntilNextRefresh =
            endOfUnit[refreshEvery](Date.now()).getTime() - Date.now() + 100; // Add 100ms to ensure we don't miss the refresh;

        if (timeUntilNextRefresh < 0) {
            // We time traveled. Shit is broke. Stop refreshing for the next hour.
            timeUntilNextRefresh = 1000 * 60 * 60 + 100;
        }

        const timeout = setTimeout(() => {
            setTimestamp(Date.now());
        }, timeUntilNextRefresh);

        // Cleanup interval on component unmount
        return () => clearTimeout(timeout);
    }, [now]);

    return now;
}

export function prettyPriceInterval(price: Price) {
    let interval: string = price.interval;
    if (price.intervalCount > 1) {
        interval = `${price.intervalCount} ${price.interval}s`;
    }
    return interval;
}
